import { useCallback, useState } from 'react';
import { Button, Dialog, DialogTrigger, Label, Text, makeStyles, tokens } from '@coreai/fluentui-react';
import { FolderOpen20Regular } from '@fluentui/react-icons';
import { useTelemetry } from '@/hooks/useTelemetry';
import { useAzureResourcePickerIdentity } from '@/hooks/useAzureResourcePicker';
import type { ValueSegment } from '@/types/valueSegment';
import { ValueSegmentType, generateSegmentId, isValueSegmentArray, segmentsToString } from '@/types/valueSegment';
import { readAzureResourcePickerOptions } from '@/utils/azureResourcePicker';
import { EndActionsSlot } from '../shared/EndActionsSlot';
import { ExpressionPreview } from './ExpressionPreview';
import { TokenizedInput } from './TokenizedInput';
import { useFieldPreviewFocus } from './useFieldPreviewFocus';
import type { StringFieldProps } from './StringField';
import { AzureResourcePickerDialog } from './AzureResourcePickerDialog';

const useStyles = makeStyles({
  container: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  inputRow: { display: 'flex', alignItems: 'flex-end', gap: tokens.spacingHorizontalXS, width: '100%' },
  inputStack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, flexGrow: 1, minWidth: 0 },
  labelRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  label: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground1 },
  required: { color: tokens.colorPaletteRedForeground1, marginLeft: tokens.spacingHorizontalXXS },
  inputWrapper: { position: 'relative' },
  browseButton: { minWidth: 'auto' },
  error: { fontSize: tokens.fontSizeBase200, color: tokens.colorPaletteRedForeground1 },
});

export interface AzureResourcePickerFieldProps extends StringFieldProps {
  editorOptions?: Record<string, unknown> | undefined;
}

function AzureResourcePickerFieldContent({
  editorOptions, name, label, value, onChange, description, placeholder, required, disabled, error,
  showAgentParameterButton, agentParameterSchema, actionOutputs, hasRunData, isStale, endActions,
}: AzureResourcePickerFieldProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const { trackEvent } = useTelemetry();
  const { containerHandlers, previewHandlers, previewToggleSlotRef } = useFieldPreviewFocus();

  const segments: ValueSegment[] = isValueSegmentArray(value)
    ? value
    : typeof value === 'string' && value
      ? [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value }]
      : [];
  const stringValue = segmentsToString(segments);
  const handleChange = useCallback((next: ValueSegment[]) => onChange(next), [onChange]);

  let options;
  try {
    options = readAzureResourcePickerOptions(editorOptions);
  } catch (configError) {
    options = undefined;
    error = configError instanceof Error ? configError.message : 'Invalid Azure resource picker settings.';
  }

  const field = (
    <div className={styles.inputStack}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <EndActionsSlot previewToggleSlotRef={previewToggleSlotRef}>{endActions}</EndActionsSlot>
      </div>
      <div className={styles.inputWrapper}>
        <TokenizedInput
          name={name}
          ariaLabel={label}
          value={segments}
          onChange={handleChange}
          placeholder={placeholder || description}
          multiline={false}
          disabled={disabled}
          readonly={false}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />
        {stringValue && (
          <ExpressionPreview
            value={stringValue}
            actionOutputs={actionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            mode="overlay"
            {...previewHandlers}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.container} {...containerHandlers}>
      <Dialog open={open && !disabled} onOpenChange={(_, data) => {
        trackEvent(data.open ? 'azureResourcePicker.opened' : 'azureResourcePicker.cancelled');
        setOpen(data.open);
      }}>
        <div className={styles.inputRow}>
          {field}
          {options && (
            <DialogTrigger disableButtonEnhancement>
              <Button className={styles.browseButton} icon={<FolderOpen20Regular />}
                aria-label="Browse Azure resources" title="Browse Azure resources"
                data-testid="browse-azure-resources" disabled={disabled ?? false} />
            </DialogTrigger>
          )}
        </div>
        {options && open && !disabled ? (
          <AzureResourcePickerDialog
            initialValue={stringValue}
            options={options}
            onSelect={(id) => {
              trackEvent('azureResourcePicker.selected');
              onChange(id);
              setOpen(false);
            }}
          />
        ) : <></>}
      </Dialog>
      {error && <Text className={styles.error} role="alert">{error}</Text>}
    </div>
  );
}

export function AzureResourcePickerField(props: AzureResourcePickerFieldProps) {
  const identity = useAzureResourcePickerIdentity();
  return <AzureResourcePickerFieldContent key={identity} {...props} />;
}